import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, Megaphone, Building2, FileSignature } from "lucide-react";
import { Wizard, WField, WNote } from "@/components/fs/Wizard";
import { V } from "@/lib/vocab";

const usd = (n) => (n === null || n === undefined || n === "" ? "—"
  : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const PAPER = { coi: "insurance certificate", w9: "W-9", agreement: "signed agreement" };
const niceDate = (d, t) => {
  if (!d) return "";
  const dt = new Date(`${d}T${t || "12:00"}:00`);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    + (t ? ` · ${dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : "");
};

/* Confirm the job (was "kickoff"). Plain questions, one screen each: price, start, contract,
   residents — and the accounting code only when this community has accounting switched on.
   Redline never drafts a contract: it's the company's paper or none. */
export default function AwardKickoffWizard({ tenantId, woId, onDone, onClose }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [rec, setRec] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/tenants/${tenantId}/field-services/wos/${woId}/kickoff`);
      setD(data);
      const w = data.work_order || {};
      setF({
        price_status: w.amount ? "agreed" : "pending", amount: w.amount ?? "", deposit: "",
        start_date: w.start_date || "", start_time: w.start_time || "",
        paper: data.contract_required ? "their_paper" : "none",
        notify_residents: false, send_schedule_email: true,
        gl_code: (data.suggested_code || {}).code || "",
      });
      api.get(`/tenants/${tenantId}/field-services/wos/${woId}/schedule-email`).then((r) => setRec(r.data)).catch(() => setRec({ to: [], cc: [] }));
    })().catch(() => toast.error("Couldn't open this job"));
  }, [tenantId, woId]);

  if (!d) return <div className="grid h-40 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-ink/30" /></div>;
  const wo = d.work_order || {};
  const gate = d.gate || {};
  const accounting = !!d.accounting;
  const amountNum = Number(f.amount || 0);
  const fee = (accounting && d.fee_policy && amountNum > 0 && f.price_status === "agreed") ? d.fee : null;

  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/field-services/wos/${woId}/kickoff`, {
        price_status: f.price_status, amount: f.price_status === "agreed" ? f.amount : undefined,
        deposit: f.price_status === "agreed" && f.deposit ? f.deposit : undefined,
        start_date: f.start_date || undefined, start_time: f.start_time || undefined,
        paper: f.paper, notify_residents: f.notify_residents, send_schedule_email: f.send_schedule_email !== false,
        gl_code: accounting ? f.gl_code : undefined,
      });
      const se = data.schedule_email;
      toast.success((data.cleared?.cleared ? `Confirmed — ${wo.vendor_name} is cleared to start`
        : f.paper === "their_paper" ? `Confirmed — waiting on ${wo.vendor_name}'s contract`
          : `Confirmed — waiting on ${wo.vendor_name}'s paperwork`)
        + (se?.sent ? `. Details emailed to ${(se.to || []).length + (se.cc || []).length} people` : ""));
      onDone?.(); onClose?.();
    } catch (e) { toast.error(errMsg(e, "Couldn't confirm this job")); }
    setBusy(false);
  };

  const steps = [
    {
      key: "company", title: "The company",
      hint: "Who's doing the work and whether their paperwork is in.",
      blocked: !wo.vendor_id ? "Assign a company first." : null,
      render: () => (
        <div className="rounded-xl border border-sand-200 bg-white p-4" data-testid="confirm-company">
          <div className="flex items-center gap-2 text-sm font-bold text-ink"><Building2 className="h-4 w-4 text-ink/45" /> {wo.vendor_name || "Nobody assigned"}</div>
          <div className="mt-1 text-xs text-ink/55">{wo.title}{wo.location ? ` · ${wo.location}` : ""}</div>
          {gate.ok ? (
            <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><Check className="h-3.5 w-3.5" /> Insurance, W-9 and agreement are on file.</div>
          ) : (
            <WNote tone="amber" testid="confirm-gate-note">
              Still missing from {wo.vendor_name}: {(gate.missing || []).map((m) => PAPER[m.key] || m.label).join(", ")}. You can confirm the job now — nobody is sent to the property until it's in, and they've been asked.
            </WNote>
          )}
        </div>
      ),
    },
    {
      key: "price", title: "Price",
      hint: "Agreed with the company, or still waiting on their number.",
      blocked: f.price_status === "agreed" && !(amountNum > 0) ? "Enter the agreed price — or say it isn't agreed yet." : null,
      render: () => (
        <>
          <div className="grid gap-2 sm:grid-cols-2">
            {[["agreed", "We agreed a price", "Enter it below. This is what invoices are checked against."],
              ["pending", "Not agreed yet", "Waiting on their quote. You can set the price later from the job."]].map(([k, t, s]) => (
              <button key={k} type="button" data-testid={`confirm-price-${k}`} onClick={() => setF({ ...f, price_status: k })}
                className={`rounded-xl border p-3.5 text-left ${f.price_status === k ? "border-ink bg-sand-50" : "border-sand-200 bg-white"}`}>
                <span className="flex items-center text-sm font-bold text-ink">{t}{f.price_status === k && <Check className="ml-auto h-4 w-4 text-emerald-600" />}</span>
                <span className="mt-1 block text-xs text-ink/55">{s}</span>
              </button>
            ))}
          </div>
          {f.price_status === "agreed" && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <WField label="Agreed price">
                <input data-testid="confirm-amount" type="number" min="0" step="0.01" value={f.amount}
                  onChange={(e) => setF({ ...f, amount: e.target.value })}
                  className="w-full rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none" />
              </WField>
              <WField label="Deposit (optional)">
                <input data-testid="confirm-deposit" type="number" min="0" step="0.01" value={f.deposit} placeholder="0.00"
                  onChange={(e) => setF({ ...f, deposit: e.target.value })}
                  className="w-full rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none" />
              </WField>
            </div>
          )}
          {fee && (
            <WNote testid="confirm-fee">Management fee on this job: <b>{usd(fee.amount)}</b> ({fee.basis}). The company never sees it.</WNote>
          )}
        </>
      ),
    },
    {
      key: "start", title: "When they start",
      hint: "Optional. A date, and a time if you have one.",
      render: () => (
        <div className="grid gap-3 sm:grid-cols-2">
          <WField label="Start date">
            <input data-testid="confirm-start-date" type="date" value={f.start_date}
              onChange={(e) => setF({ ...f, start_date: e.target.value })}
              className="w-full rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none" />
          </WField>
          <WField label="Time (optional)">
            <input data-testid="confirm-start-time" type="time" value={f.start_time} disabled={!f.start_date}
              onChange={(e) => setF({ ...f, start_time: e.target.value })}
              className="w-full rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none disabled:bg-sand-100" />
          </WField>
          {f.start_date && <p className="text-xs text-ink/55 sm:col-span-2">Starts {niceDate(f.start_date, f.start_time)}.</p>}
        </div>
      ),
    },
    {
      key: "contract", title: "Contract",
      hint: "Their paper or none. Ask Redline doesn't draft contracts.",
      render: () => (
        <div className="grid gap-2 sm:grid-cols-2">
          {[["their_paper", "The company's contract", "They upload it from their Redline page; you read it, then it goes out for signature."],
            ["none", "No contract", d.contract_required
              ? `This job is over the ${usd(d.no_contract_under)} threshold this ${V.lc("org")} set — proceed on the work order alone only if you mean to.`
              : `Under this ${V.lc("org")}'s ${usd(d.no_contract_under)} threshold — the work order is enough.`]].map(([k, t, s]) => (
            <button key={k} type="button" data-testid={`confirm-paper-${k}`} onClick={() => setF({ ...f, paper: k })}
              className={`rounded-xl border p-3.5 text-left ${f.paper === k ? "border-ink bg-sand-50" : "border-sand-200 bg-white"}`}>
              <span className="flex items-center gap-2 text-sm font-bold text-ink"><FileSignature className="h-4 w-4 text-ink/45" /> {t}{f.paper === k && <Check className="ml-auto h-4 w-4 text-emerald-600" />}</span>
              <span className="mt-1 block text-xs text-ink/55">{s}</span>
            </button>
          ))}
        </div>
      ),
    },
    {
      key: "tell", title: `${V.many("person")}`,
      hint: `Should ${V.lcs("person")} hear about this work before a crew shows up?`,
      render: () => (
        <button data-testid="confirm-notify" onClick={() => setF({ ...f, notify_residents: !f.notify_residents })}
          className={`flex w-full items-center justify-between gap-3 rounded-xl border p-3.5 text-left transition-colors ${f.notify_residents ? "border-emerald-200 bg-emerald-50" : "border-sand-200 bg-white"}`}>
          <span className="flex items-center gap-2 text-sm font-bold text-ink">
            <Megaphone className={`h-4 w-4 ${f.notify_residents ? "text-emerald-600" : "text-ink/40"}`} />
            Draft {V.a("person")} notice for me to review
          </span>
          <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${f.notify_residents ? "bg-emerald-600 text-white" : "bg-sand-200 text-ink/60"}`}>
            {f.notify_residents ? "Yes" : "No"}
          </span>
        </button>
      ),
    },
    ...(accounting ? [{
      key: "code", title: "Accounting code",
      hint: "Every invoice on this job inherits it. The company never sees it.",
      blocked: !f.gl_code ? "Pick the code this job is charged to." : null,
      render: () => (
        <WField label="Charge to">
          <select data-testid="confirm-code" value={f.gl_code} onChange={(e) => setF({ ...f, gl_code: e.target.value })}
            className="w-full rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-sm outline-none">
            <option value="">Pick a code…</option>
            {(d.codes || []).map((c) => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
          </select>
        </WField>
      ),
    }] : []),
    {
      key: "review", title: "Review",
      render: () => (
        <div className="rounded-xl border border-sand-200 bg-white p-4 text-sm text-ink/75" data-testid="confirm-review">
          <div className="font-bold text-ink">{wo.title}</div>
          <div className="mt-2 space-y-1 text-xs">
            <div>{wo.vendor_name}{gate.ok ? " · paperwork complete" : " · paperwork still coming in"}</div>
            <div>{f.price_status === "agreed" ? `Price ${usd(f.amount)}${f.deposit ? ` · deposit ${usd(f.deposit)}` : ""}` : "Price not agreed yet"}</div>
            {fee && <div>Management fee {usd(fee.amount)}</div>}
            <div>{f.start_date ? `Starts ${niceDate(f.start_date, f.start_time)}` : "No start date set"}</div>
            <div>{f.paper === "their_paper" ? "Their contract, then signatures" : "No contract"}</div>
            <div>{f.notify_residents ? `${V.A("person")} notice will be drafted for you` : `No ${V.lc("person")} notice`}</div>
            {accounting && <div>Charged to {f.gl_code}</div>}
          </div>
          <WNote tone={gate.ok ? "emerald" : "amber"} testid="confirm-review-gate">
            {gate.ok ? `${wo.vendor_name} is cleared to start the moment you confirm.`
              : `${wo.vendor_name} can't be sent to the property until we have their ${(gate.missing || []).map((m) => PAPER[m.key] || m.label).join(" and ")}. Confirm now; it starts itself when that arrives.`}
          </WNote>
          {rec && (rec.to.length + rec.cc.length > 0) && (
            <label data-testid="confirm-schedule-email" className={`mt-2 flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 text-xs leading-relaxed ${f.send_schedule_email !== false ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-sand-200 bg-white text-ink/55"}`}>
              <input type="checkbox" data-testid="confirm-schedule-email-toggle" className="mt-0.5" checked={f.send_schedule_email !== false}
                onChange={(e) => setF({ ...f, send_schedule_email: e.target.checked })} />
              <span>
                <b>Email everyone the details</b> — scope, price, start, contract, who to call. To: {rec.to.join(", ") || "—"}{rec.cc.length ? <> · cc: {rec.cc.join(", ")}</> : ""}.
                <span className="block text-[11px] opacity-70">Change who's copied under Field Services → Settings.</span>
              </span>
            </label>
          )}
        </div>
      ),
    },
  ];

  return (
    <Wizard testid="wiz-kickoff" steps={steps} busy={busy} onFinish={go} onClose={onClose}
      finishLabel="Confirm the job" finishIcon={Check} />
  );
}
